/**
 * Database Service
 * A service that provides access to the database, either Firestore or local storage
 */
import * as LocalDB from '../utils/localStorageDB';
import { db } from '../firebase-config';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { getDocument as getFirestoreDocument, setDocument as setFirestoreDocument, queryDocuments } from './firestoreService';
import { disableNetwork, enableNetwork } from 'firebase/firestore';

// Use Firestore by default
let useFirestore = true;

/**
 * Initialize the database service
 */
export const initDatabaseService = () => {
  // Start with Firestore enabled by default
  useFirestore = true;
  console.log('Database service initialized with Firestore mode');
  
  // Return a cleanup function
  return () => {
    console.log('Database service cleaned up');
  };
};

/**
 * Configure whether to use Firestore or local storage
 */
export const setUseFirestore = (value) => {
  const previous = useFirestore;
  useFirestore = value;
  console.log(`Database mode changed: ${previous ? 'Firestore' : 'Local Storage'} -> ${value ? 'Firestore' : 'Local Storage'}`);
};

/**
 * Get the current database mode
 */
export const getUseFirestore = () => useFirestore;

/**
 * Get a document from Firestore or local storage
 */
export const getDocument = async (collectionName, id) => {
  if (!collectionName || !id) {
    throw new Error('Collection and ID are required');
  }
  
  console.log(`Getting document ${collectionName}/${id} using Firestore`);
  
  try {
    // Use the Firebase SDK with default database
    const docRef = doc(db, collectionName, id);
    const docSnap = await getDoc(docRef);
    
    // Return in a consistent format
    return {
      exists: () => docSnap.exists(),
      data: () => docSnap.exists() ? docSnap.data() : null,
      id: id
    };
  } catch (error) {
    console.error(`Error getting document ${collectionName}/${id}:`, error);
    
    // Fall back to local storage
    console.log(`Falling back to local storage for ${collectionName}/${id}`);
    const localData = LocalDB.getDoc(collectionName, id);
    
    if (localData) {
      return {
        exists: () => true,
        data: () => localData,
        id: id
      };
    }
    
    return {
      exists: () => false,
      data: () => null,
      id: id
    };
  }
};

/**
 * Set a document in Firestore or local storage
 */
export const setDocument = async (collectionName, id, data) => {
  if (!collectionName || !id) {
    throw new Error('Collection and ID are required');
  }
  
  console.log(`Setting document ${collectionName}/${id} using Firestore`);
  
  // Always save to local storage as a backup
  LocalDB.setDoc(collectionName, id, data);
  
  try {
    // Use the Firebase SDK with default database
    const docRef = doc(db, collectionName, id);
    await setDoc(docRef, data);
    return true;
  } catch (error) {
    console.error(`Error setting document ${collectionName}/${id}:`, error);
    // Log more detailed error information
    if (error.code) {
      console.error(`Error code: ${error.code}`);
    }
    return false;
  }
};

/**
 * New methods that use the proxy service
 */
export const getUserData = async (userId) => {
  const result = await getFirestoreDocument('users', userId);
  return result.exists ? result.data : null;
};

export const updateUserData = async (userId, data) => {
  const result = await setFirestoreDocument('users', userId, data);
  return result.success;
};

export const getBusinessesByOwner = async (ownerId) => {
  return await queryDocuments('businesses', 'ownerId', ownerId);
};

/**
 * Get all documents in a collection
 */
export const getCollection = async (collection) => {
  if (!collection) {
    throw new Error('Collection is required');
  }
  
  console.log(`Getting collection ${collection} from ${useFirestore ? 'Firestore' : 'local storage'}`);
  
  // For now, we only support local storage collections
  return LocalDB.getCollection(collection);
};

/**
 * Get database stats
 */
export const getDatabaseStats = () => {
  return LocalDB.getDatabaseStats();
};

// Add these functions to control network connectivity
export const goOffline = async () => {
  try {
    await disableNetwork(db);
    console.log('Firebase network connections disabled');
    return true;
  } catch (error) {
    console.error('Error disabling network:', error);
    return false;
  }
};

export const goOnline = async () => {
  try {
    await enableNetwork(db);
    console.log('Firebase network connections enabled');
    return true;
  } catch (error) {
    console.error('Error enabling network:', error);
    return false;
  }
};
